import { Close } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  approveFacilityReport,
  getFacilityReport,
} from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

export default function FinalizeResult({
  open,
  title,
  onClose,
  facilityId,
  reportQuarter,
  reportYear,
}) {
  const [report, setReport] = useState();
  const [reportLoading, setReportLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // console.log('report', report);

  useEffect(() => {
    fetchReport();
  }, []);

  const handleApprove = async () => {
    setSaving(true);

    try {
      const payload = {
        reportID: report?.reportId,
        emailMessage: '',
        recipients: [],
      };
      const res = await approveFacilityReport(payload);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errMsg = error?.data?.result?.status?.desc;
      openNotification({
        type: 'error',
        message: !errMsg ? 'Something went wrong. Please try again' : errMsg,
      });
    } finally {
      setSaving(false);
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const res = await getFacilityReport(facilityId, reportQuarter, reportYear);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setReport(data);
      }
    } catch (error) {
    } finally {
      setReportLoading(false);
    }
  };

  const handleRenderContent = () => {
    if (reportLoading) {
      return <LoaderSkeleton />;
    }

    if (!reportLoading && !report) {
      return null;
    }

    if (!reportLoading && report) {
      return renderContent();
    }
  };

  const renderContent = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 600 }}>Confirm Finalization</AlertTitle>
          <Typography variant="body2" gutterBottom>
            You are about to finalize and publish <strong>{report?.reportType}</strong> results for{' '}
            <strong>{report?.facility}</strong>.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
            <em>Note: If this action is completed, results can no longer be edited.</em>
          </Typography>
        </Alert>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 } // Softens the entire dialog box
      }}
    >
      {/* Dialog Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        <DialogTitle variant="h6" fontWeight={700} sx={{ p: 0 }}>
          {title || 'Finalize Result'}
        </DialogTitle>
        <IconButton 
          onClick={onClose} 
          disabled={saving}
          sx={{ color: 'text.secondary' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Dialog Content */}
      <DialogContent sx={{ px: 3, py: 1 }}>
        {handleRenderContent()}
      </DialogContent>

      {/* Dialog Footer */}
      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5, 
          borderTop: 1, 
          borderColor: 'divider',
          gap: 1 
        }}
      >
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          disabled={saving}
          onClick={onClose}
          sx={{ 
            textTransform: 'none', 
            borderRadius: 2,
            px: 3,
            borderColor: 'divider' 
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disableElevation
          disabled={saving}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
          sx={{ 
            textTransform: 'none', 
            borderRadius: 2,
            px: 4
          }}
          onClick={handleApprove}
        >
          {saving ? 'Finalizing...' : 'Finalize'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}